import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { translations, defaultLanguage, supportedLanguages } from './translations';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Try to get from localStorage, then browser, then default
    const stored = localStorage.getItem('tontine_language');
    if (stored && translations[stored]) return stored;
    
    const browserLang = navigator.language.split('-')[0];
    if (translations[browserLang]) return browserLang;
    
    return defaultLanguage;
  });

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem('tontine_language', lang);
      document.documentElement.lang = lang;
      axios.defaults.headers.common['Accept-Language'] = lang;
    }
  }, []);

  useEffect(() => {
    const handleExternalLanguageSync = (event) => {
      const nextLanguage = event?.detail?.language;
      if (!nextLanguage || !translations[nextLanguage]) {
        return;
      }
      setLanguageState(nextLanguage);
      localStorage.setItem('tontine_language', nextLanguage);
      document.documentElement.lang = nextLanguage;
      axios.defaults.headers.common['Accept-Language'] = nextLanguage;
    };

    window.addEventListener('tontine-language-sync', handleExternalLanguageSync);
    return () => window.removeEventListener('tontine-language-sync', handleExternalLanguageSync);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
    axios.defaults.headers.common['Accept-Language'] = language;
  }, [language]);

  // Get translation by key path (e.g., "nav.dashboard")
  const t = useCallback((keyPath, params = {}) => {
    const keys = keyPath.split('.');
    let value = translations[language];
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key];
      } else {
        // Fallback to French if key not found
        value = translations[defaultLanguage];
        for (const k of keys) {
          if (value && typeof value === 'object' && k in value) {
            value = value[k];
          } else {
            return keyPath; // Return key if not found
          }
        }
        break;
      }
    }
    
    // Replace params like {name} with actual values
    if (typeof value === 'string' && Object.keys(params).length > 0) {
      return value.replace(/\{(\w+)\}/g, (match, key) => (
        Object.prototype.hasOwnProperty.call(params, key) ? params[key] : match
      ));
    }
    
    return value || keyPath;
  }, [language]);

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      t,
      supportedLanguages,
      translations: translations[language],
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageProvider;
